import { useEffect, useRef, useState } from 'react';
import { Crosshair, Eye, EyeOff, MapPin, MapPinPlus, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button';
import { overallStage } from '@/lib/review-stage';
import type { DecisionKind, DraftNote, NoteView, ViewState } from '@/types';

/**
 * The conversation about this design, anchored to the model.
 *
 * A pinned note is worth more than a loose one: "the contact is tight" and "the
 * contact is tight HERE" are different instructions, and only one saves the
 * technician a guess. Each pin keeps the camera and the layer state it was
 * written from, so "Show me" puts the reader exactly where the writer was -
 * same angle, same ghosted antagonist.
 *
 * Notes are not sent one by one. The dentist adds them here and they go to the
 * technician with the decision, as one round, each under a stage: a note the
 * dentist wrote says itself whether it is about approving, a change or a
 * rejection; a suggested reply goes under the decision they send.
 *
 * Clicking a pin on the model scrolls its note into view and flashes it, so the
 * connection between the number on the crown and the words is unmistakable.
 * The pins themselves can be hidden when they get in the way of looking.
 */

/**
 * One-tap starts for the requests a dentist makes most, each with the status
 * it stands for: "Looks good" approves, the others ask for a change. They fill
 * the box rather than adding, so the dentist can add the tooth or the amount;
 * the status is picked for them and can still be changed.
 */
const QUICK_REPLIES: { text: string; stage: DecisionKind }[] = [
  { text: 'Open the contact slightly', stage: 'changes_requested' },
  { text: 'Tighten the contact', stage: 'changes_requested' },
  { text: 'Reduce the occlusion', stage: 'changes_requested' },
  { text: 'Extend the margin', stage: 'changes_requested' },
  { text: 'Thicken the wall here', stage: 'changes_requested' },
  { text: 'Looks good', stage: 'approve' },
];

const STAGES: { value: DecisionKind; label: string; on: string; off: string }[] = [
  {
    value: 'approve',
    label: 'Approve',
    on: 'border-transparent bg-success-500 text-white',
    off: 'border-success-500/30 text-success-600 hover:bg-success-50',
  },
  {
    value: 'changes_requested',
    label: 'Changes',
    on: 'border-transparent bg-warning-500 text-white',
    off: 'border-warning-500/30 text-warning-600 hover:bg-warning-50',
  },
  {
    value: 'reject',
    label: 'Reject',
    on: 'border-transparent bg-danger-500 text-white',
    off: 'border-danger-500/30 text-danger-500 hover:bg-danger-50',
  },
];

const STAGE_BADGE: Record<DecisionKind, string> = {
  approve: 'bg-success-50 text-success-600',
  changes_requested: 'bg-warning-50 text-warning-600',
  reject: 'bg-danger-50 text-danger-600',
};

const STAGE_LABEL: Record<DecisionKind, string> = {
  approve: 'Approve',
  changes_requested: 'Changes',
  reject: 'Reject',
};

interface NotesThreadProps {
  /** Sent notes, then the dentist's drafts (draft: true). */
  notes: NoteView[];
  activeId: string | null;
  /** Briefly highlighted - the note whose pin was just clicked. */
  flashId: string | null;
  onActivate: (note: NoteView) => void;
  showPins: boolean;
  onShowPins: (show: boolean) => void;
  /** A spot the dentist clicked on the model, waiting for its note. */
  pending: { position: [number, number, number]; view: ViewState } | null;
  onClearPending: () => void;
  pinning: boolean;
  onPin: () => void;
  /** Adds a note to the round; it is sent with the decision. */
  onAddDraft: (note: Omit<DraftNote, 'id' | 'createdAt'>) => void;
  onRemoveDraft: (id: string) => void;
  /** A decided review is closed to new notes. */
  closed: boolean;
  /** Why commenting is not open yet (no name or clinic at the top), or null. */
  needDetails: string | null;
}

/** The status of the suggestions in the box: what is actually there now. */
function stageOfReplies(text: string): DecisionKind | null {
  const lower = text.toLowerCase();
  return overallStage(
    QUICK_REPLIES.filter((reply) => lower.includes(reply.text.toLowerCase())).map(
      (reply) => reply.stage,
    ),
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotesThread({
  notes,
  activeId,
  flashId,
  onActivate,
  showPins,
  onShowPins,
  pending,
  onClearPending,
  pinning,
  onPin,
  onAddDraft,
  onRemoveDraft,
  closed,
  needDetails,
}: NotesThreadProps) {
  const [text, setText] = useState('');
  // Every comment carries a status. The suggestions in the box set it ("Looks
  // good" approves, the others ask for changes) - read from the text as it is
  // now, so a suggestion deleted from the box takes its status with it. A
  // comment the dentist writes has none until they pick one, and a pick of
  // theirs wins until the box is cleared or another suggestion is added.
  const [picked, setPicked] = useState<DecisionKind | null>(null);
  const suggested = stageOfReplies(text);
  const stage = picked ?? suggested;
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const canAdd = !needDetails && text.trim().length > 0 && stage !== null;
  const pinnedCount = notes.filter((note) => note.position).length;
  const drafts = notes.filter((note) => note.draft).length;

  // Bring the flashed note into view; a long thread would otherwise flash it
  // somewhere off-screen, which is the same as not flashing it.
  useEffect(() => {
    if (flashId)
      itemRefs.current.get(flashId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [flashId]);

  // A note just added shows where it went: the list scrolls to it, rather
  // than leaving it below the fold while the composer clears.
  const lastDraft = notes.filter((note) => note.draft).at(-1)?.id ?? null;
  const addedRef = useRef(false);
  useEffect(() => {
    if (!addedRef.current || !lastDraft) return;
    addedRef.current = false;
    itemRefs.current.get(lastDraft)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [lastDraft]);

  const add = () => {
    if (!canAdd) return;
    addedRef.current = true;
    onAddDraft({
      text: text.trim(),
      stage,
      position: pending?.position ?? null,
      view: pending?.view ?? null,
    });
    setText('');
    setPicked(null);
    onClearPending();
  };

  const addReply = (reply: { text: string }) => {
    setText(text.trim() ? `${text.trimEnd()} ${reply.text}.` : `${reply.text}.`);
    // The suggestions now in the box decide the status again.
    setPicked(null);
  };

  return (
    // Scrolls as a whole when the decision form below takes the height, so the
    // composer is never cut off under it.
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {pinnedCount > 0 && (
        <div className="flex items-center justify-between border-b border-border px-4 py-1.5">
          <span className="text-[11.5px] text-muted-foreground">
            {pinnedCount} pinned on the model
          </span>
          <button
            type="button"
            onClick={() => {
              onShowPins(!showPins);
            }}
            aria-pressed={!showPins}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {showPins ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
            {showPins ? 'Hide pins' : 'Show pins'}
          </button>
        </div>
      )}

      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {notes.length === 0 && (
          <li className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
            <MapPin className="mx-auto mb-2 size-5 text-muted-foreground" />
            <p className="text-sm font-medium">No notes yet</p>
            <p className="mt-1 text-[12.5px] text-muted-foreground">
              Pin a note to the spot it is about. Your notes go to the technician with your
              decision.
            </p>
          </li>
        )}

        {notes.map((note) => {
          const active = note.id === activeId;
          const flashing = note.id === flashId;
          const lab = note.authorRole === 'lab';
          return (
            <li
              key={note.id}
              className="relative"
              ref={(element) => {
                if (element) itemRefs.current.set(note.id, element);
                else itemRefs.current.delete(note.id);
              }}
            >
              <button
                type="button"
                onClick={() => {
                  onActivate(note);
                }}
                className={cn(
                  'group w-full rounded-lg border p-3 text-left transition-colors',
                  note.draft && 'border-dashed',
                  active
                    ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/30'
                    : 'border-border hover:bg-muted/60',
                  flashing && 'note-flash',
                )}
              >
                <div className={cn('mb-1.5 flex items-center gap-2', note.draft && 'pr-6')}>
                  {note.pin !== null ? (
                    <span
                      className={cn(
                        'flex size-5 shrink-0 items-center justify-center rounded-full text-[10.5px] font-semibold text-white',
                        lab ? 'bg-neutral-700' : 'bg-rose-600',
                      )}
                    >
                      {note.pin}
                    </span>
                  ) : (
                    <span className="size-5 shrink-0 rounded-full border border-dashed border-neutral-300" />
                  )}
                  {note.stage ? (
                    <span
                      className={cn(
                        'rounded px-1.5 py-px text-[10px] font-semibold tracking-wide uppercase',
                        STAGE_BADGE[note.stage],
                      )}
                    >
                      {STAGE_LABEL[note.stage]}
                    </span>
                  ) : note.draft ? (
                    <span className="rounded bg-muted px-1.5 py-px text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                      No status
                    </span>
                  ) : null}
                  {!note.draft && (
                    <span className="truncate text-[13px] font-semibold">{note.authorName}</span>
                  )}
                  <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                    {note.draft ? 'Not sent yet' : formatTime(note.createdAt)}
                  </span>
                </div>
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-foreground/85">
                  {note.text}
                </p>
                {note.view && (
                  <span className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-primary opacity-80 group-hover:opacity-100">
                    <Crosshair className="size-3" />
                    Show me this view
                  </span>
                )}
              </button>
              {note.draft && !closed && (
                <button
                  type="button"
                  aria-label="Remove this note"
                  title="Remove"
                  onClick={() => {
                    onRemoveDraft(note.id);
                  }}
                  className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {!closed && needDetails && (
        // Every comment goes out under the dentist's name and clinic, so the
        // box opens once both are filled in at the top.
        <div className="border-t border-border bg-muted/30 px-4 py-4 text-center">
          <p className="text-[12.5px] font-medium text-warning-600">{needDetails}</p>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Your comments go to the lab under your name and clinic.
          </p>
        </div>
      )}

      {!closed && !needDetails && (
        <div className="space-y-2 border-t border-border bg-muted/30 px-4 py-3">
          {pending ? (
            <div className="flex items-center gap-2 rounded-md bg-rose-50 px-2.5 py-1.5 text-[12px] text-rose-800">
              <MapPin className="size-3.5 shrink-0" />
              Pinned to the model. This note will point at that spot.
              <button
                type="button"
                onClick={onClearPending}
                aria-label="Remove the pin"
                className="ml-auto rounded p-0.5 hover:bg-rose-100"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onPin}
              className={cn(
                'flex w-full items-center gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-[12px] font-medium transition-colors',
                pinning
                  ? 'border-rose-400 bg-rose-50 text-rose-800'
                  : 'border-border text-muted-foreground hover:border-rose-300 hover:text-rose-700',
              )}
            >
              <MapPinPlus className="size-3.5" />
              {pinning
                ? 'Now click the spot on the model…'
                : 'Pin this note to a spot on the model'}
            </button>
          )}

          <div className="flex flex-wrap gap-1.5">
            {QUICK_REPLIES.map((reply) => (
              <button
                key={reply.text}
                type="button"
                onClick={() => {
                  addReply(reply);
                }}
                className="shrink-0 rounded-full border border-border bg-background px-2.5 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                {reply.text}
              </button>
            ))}
          </div>

          <textarea
            rows={3}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              if (!event.target.value.trim()) setPicked(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) add();
            }}
            placeholder="e.g. Mesial contact looks tight against 44 — can it open slightly?"
            aria-label="Note"
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
          />

          {text.trim() && (
            <div className="space-y-1.5">
              <p className="text-[11.5px] text-muted-foreground">
                Status of this comment
                {picked === null && suggested && ' - from the suggestion'}
              </p>
              <div
                role="radiogroup"
                aria-label="Note is about"
                className="grid grid-cols-3 gap-1.5"
              >
                {STAGES.map((option) => {
                  const on = stage === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => {
                        setPicked(option.value);
                      }}
                      className={cn(
                        'h-7 rounded-md border text-[12px] font-semibold transition-colors',
                        on ? option.on : `bg-background ${option.off}`,
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button onClick={add} disabled={!canAdd} variant="outline" className="w-full">
            <Plus />
            Add comment
          </Button>
          {text.trim() && !canAdd && (
            <p className="text-center text-[11.5px] font-medium text-warning-600">
              Choose Approve, Changes or Reject for this comment to add it.
            </p>
          )}
          {drafts > 0 && (
            <p className="text-center text-[11px] text-muted-foreground">
              {`${String(drafts)} comment${drafts === 1 ? '' : 's'} ready - send them below.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
